import { z } from "zod";

export const teamSpendGraphAndSummary = z.object({
  summary: z.object({
    total_spend: z.number(),
    active_teams: z.number(),
    average_daily_spend: z.number(),
    unaccounted_spend: z.number(),
  }),
  spend_by_teams: z.array(
    z.object({
      date: z.string(), // ISO date string
      team_spends: z.record(z.string(), z.number()), // Key-value pairs for team spends
    })
  ),
});

/**
 * Schema for team spend total spends API response
 *
 * This schema includes both domain-specific fields and pagination metadata:
 * - `total_spend_by_teams`: Legacy field containing the team spend data
 * - `items`: Standard pagination field containing the same team spend data
 * - `total`, `page`, `pages`, `size`: Pagination metadata fields
 *
 * Note: The API returns both `total_spend_by_teams` and `items` with identical data
 * for backward compatibility. The component uses the `items` field for consistency
 * with other paginated endpoints.
 */
export const teamSpendTotalSpendsSchema = z.object({
  total_spend_by_teams: z
    .array(
      z.object({
        team: z.string(),
        team_spends: z.number(),
        percentage_spend: z.number(),
        percentage_change_from_previous_period: z.number().nullable(),
      })
    )
    .optional(),
  items: z.array(
    z.object({
      team: z.string(),
      team_spends: z.number(),
      percentage_spend: z.number(),
      percentage_change_from_previous_period: z.number().nullable(),
    })
  ),
  total: z.number(),
  page: z.number(),
  pages: z.number(),
  size: z.number(),
});

export const teamSpendFilters = z.object({
  teams: z.array(z.string()),
});

export type TeamSpendGraphAndSummary = z.infer<typeof teamSpendGraphAndSummary>;
export type TeamSpendTotalSpends = z.infer<typeof teamSpendTotalSpendsSchema>;
export type TeamSpendTotalSpendItem = TeamSpendTotalSpends["items"][number];
export type TeamSpendFilters = z.infer<typeof teamSpendFilters>;
export type TeamSpendQueryParams = {
  end_date?: string;
  start_date?: string;
  order_by?: string;
  order_direction?: string;
  teams?: string[];
  page?: number;
  page_size?: number;
  instance_id?: string;
};

// Test helper types (used by TeamSpend.test.tsx mocks)
export type TeamSpendMockData = {
  filters?: TeamSpendFilters;
  graphData?: TeamSpendGraphAndSummary;
  totalSpendsData?: { items: TeamSpendTotalSpendItem[]; total: number; pages: number };
};

// Resolved mock data with all required properties (returned by createTeamSpendMockResponse)
export type ResolvedTeamSpendMockData = Required<TeamSpendMockData>;

export const GET_TEAM_SPEND_GRAPH_AND_SUMMARY = "GET-TEAM-SPEND-GRAPH-AND-SUMMARY";
export const GET_TEAM_TOTAL_SPENDS = "GET-TEAM-SPEND-TOTAL-SPENDS";
export const GET_TEAM_SPEND_FILTERS = "GET-TEAM-SPEND-FILTERS";
